import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface SafetyDataSheetDialogProps {
  data: any
  shipmentId: string
  documentId: string
  onSave: (data: any, changes?: any[]) => void
}

const SafetyDataSheetDialog: React.FC<SafetyDataSheetDialogProps> = ({ data, shipmentId, documentId, onSave }) => {
  // Define sections for Data Sheet
  const sections: DocumentSection[] = [
    {
      id: 'documentInfo',
      title: 'Document Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Product ID', path: 'productId' },
        { type: 'field', label: 'Document Number', path: 'documentNumber' },
        { type: 'field', label: 'Revision Number', path: 'revisionNumber' },
        { type: 'field', label: 'Issue Date', path: 'issueDate' },
        { type: 'field', label: 'Effective Date', path: 'effectiveDate' },
        { type: 'field', label: 'Expiry Date', path: 'expiryDate' },
        { type: 'field', label: 'Prepared By', path: 'preparedBy' },
        { type: 'field', label: 'Approved By', path: 'approvedBy' },
      ],
    },
    {
      id: 'productInfo',
      title: 'Product Information',
      layout: 'double',
      fields: [
        { type: 'field', label: 'Product Name', path: 'productName' },
        { type: 'field', label: 'Product Category', path: 'productCategory' },
        { type: 'field', label: 'Product Description', path: 'productDescription', fullWidth: true },
      ],
    },
    {
      id: 'manufacturer',
      title: 'Manufacturer',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Manufacturer',
          path: 'manufacturer',
          fields: [
            { label: 'Name', path: 'name' },
            { label: 'Address', path: 'address' },
            { label: 'Contact', path: 'contact' },
          ],
        },
      ],
    },
    {
      id: 'specifications',
      title: 'Technical Specifications',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Specifications',
          path: 'specifications',
          columns: [
            { label: 'Name', path: 'name' },
            { label: 'Value', path: 'value' },
            { label: 'Unit', path: 'unit' },
          ],
        },
      ],
    },
    {
      id: 'dimensions',
      title: 'Physical Properties',
      layout: 'grid',
      fields: [
        {
          type: 'object',
          label: 'Dimensions',
          path: 'dimensions',
          fields: [
            { label: 'Length Value', path: 'length.value' },
            { label: 'Length Unit', path: 'length.unit' },
            { label: 'Width Value', path: 'width.value' },
            { label: 'Width Unit', path: 'width.unit' },
            { label: 'Height Value', path: 'height.value' },
            { label: 'Height Unit', path: 'height.unit' },
            { label: 'Diameter Value', path: 'diameter.value' },
            { label: 'Diameter Unit', path: 'diameter.unit' },
          ],
          optional: true,
        },
        {
          type: 'object',
          label: 'Weight',
          path: 'weight',
          fields: [
            { label: 'Value', path: 'value' },
            { label: 'Unit', path: 'unit' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'materials',
      title: 'Materials',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Materials',
          path: 'materials',
          columns: [
            { label: 'Name', path: 'name' },
            { label: 'Percentage', path: 'percentage' },
            { label: 'Properties', path: 'properties' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'performanceData',
      title: 'Performance Data',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Performance Data',
          path: 'performanceData',
          columns: [
            { label: 'Parameter', path: 'parameter' },
            { label: 'Value', path: 'value' },
            { label: 'Unit', path: 'unit' },
            { label: 'Test Method', path: 'testMethod' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'safetyInformation',
      title: 'Safety Information',
      layout: 'single',
      fields: [
        {
          type: 'object',
          label: 'Safety Information',
          path: 'safetyInformation',
          fields: [
            { label: 'Hazards', path: 'hazards' },
            { label: 'Precautions', path: 'precautions' },
            { label: 'Storage Requirements', path: 'storageRequirements' },
            { label: 'Disposal Instructions', path: 'disposalInstructions' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'certifications',
      title: 'Certifications',
      layout: 'single',
      fields: [
        {
          type: 'array',
          label: 'Certifications',
          path: 'certifications',
          columns: [
            { label: 'Name', path: 'name' },
            { label: 'Issuing Body', path: 'issuingBody' },
            { label: 'Certification Number', path: 'certificationNumber' },
            { label: 'Issue Date', path: 'issueDate' },
            { label: 'Expiry Date', path: 'expiryDate' },
          ],
          optional: true,
        },
      ],
    },
    {
      id: 'compliance',
      title: 'Compliance Standards',
      layout: 'single',
      fields: [{ type: 'field', label: 'Compliance Standards', path: 'complianceStandards', fullWidth: true }],
    },
    {
      id: 'usage',
      title: 'Usage Information',
      layout: 'single',
      fields: [
        { type: 'field', label: 'Application Areas', path: 'applicationAreas', fullWidth: true },
        { type: 'field', label: 'Instructions', path: 'instructions', fullWidth: true },
        { type: 'field', label: 'Limitations', path: 'limitations', fullWidth: true },
      ],
    },
  ]

  return (
    <DocumentDialog
      documentData={data}
      schema={{}}
      sections={sections}
      documentType="Safety Data Sheet"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default SafetyDataSheetDialog
